import req from '../../api/request'
import type { CountryLocaleInput } from '../../currency'

/**
 * SN-4709 Team Balance API contract (api2 PLAN-4709-v2.html §10).
 *
 * ALL MONEY IS IN DOLLARS end-to-end — conversion to minor units happens only
 * at the display boundary (Price). All ids are uids. Every mutation returns the
 * refreshed balance payload so KPIs stay consistent in one round trip.
 *
 * Mutations are NOT idempotent server-side, so they run with a single
 * transport attempt (transportRetries: 1) and react-query retry: 0 — a
 * committed-but-dropped response must never be re-sent.
 *
 * Errors are rethrown untouched: req() attaches status / statusCode /
 * invalid_fields to the thrown Error, and callers branch on them (403 = probe
 * silence, 422 = field-keyed guard message).
 */

/**
 * A slot's LIFECYCLE (Jeff, Oct 1 — kept apart from its type, `BalanceSlotType`).
 * `confirmed` is shown as "Invoiced". `invited` is an unoccupied slot held for a
 * live invite; an older api2 never sends it.
 */
export type BalanceSlotStatus =
  | 'open'
  | 'invited'
  | 'pending'
  | 'confirmed'
  | 'paid'
  | 'cancelled'

/**
 * Jeff, Oct 1 — who may fill a player slot: anyone (`player`), anyone and it is
 * advertised in the Player Finder (`player_finder`), or the one person it is held
 * for (`invited`, also after they registered into it). Null on an official's line;
 * absent on an older api2. Read only through `balanceRowKind.slotType`.
 */
export type BalanceSlotType = 'player' | 'player_finder' | 'invited'

/**
 * Who an unclaimed invited slot is held for, as the rep typed it. Never the invite
 * link's token: roster officials can read the sheet.
 */
export interface BalanceSlotInvitee {
  name_first: string | null
  name_last: string | null
  email: string | null
  /** When the invite was last sent. */
  invited_at: string | null
  /** False during the 10-minute resend cooldown. */
  can_resend: boolean
  resend_available_at: string | null
  /**
   * The position approval rosters them with unless they pick their own: the one the
   * rep set on the sheet (Inon, Oct 2), else the one they played last season when
   * they were imported (Jeff, Oct 1). Null when neither; absent on an older api2.
   */
  position?: BalanceSlotPosition | null
  /**
   * Inon, Oct 2 — the jersey number the rep set on the sheet, which approval rosters
   * them with unless they give their own. Null when none; absent on an older api2.
   */
  player_number?: number | null
  /** Jeff, Oct 1 — the previous season they were imported from; null on a typed hold. */
  from?: BalanceSlotInviteSource | null
}

/** Jeff, Oct 1 — the previous season an imported invitee played. */
export interface BalanceSlotInviteSource {
  /** That season's roster uid. */
  season_id: string
  schedule_name: string | null
  team_name: string | null
}

/** One person to hold a slot for. */
export interface InviteeInput {
  name_first: string
  name_last: string
  email: string
}

/**
 * Sep 23 — what a Team Budget line is: one PLAYER's share, or a team OFFICIAL's own
 * budget line (a Team Rep, the registering rep, or a Daysmart manager). An official
 * line is invoiced like a player's share, but as ONE charge due the day it is sent,
 * and its amount comes out of the team fee.
 *
 * Read only through `web/src/features/team-balance/utils/balanceRowKind` — a missing
 * `kind` (an older api2) means `player`.
 */
export type BalanceLineKind = 'player' | 'official'

/**
 * Sep 23 — who an official line belongs to (`slot.official`). Named by the server's
 * display rule: the official record's name while that record still belongs to the
 * payer, otherwise the payer's own name.
 */
export interface BalanceSlotOfficial {
  payer_user_id: string | null
  team_official_uid: string | null
  name: string
  /**
   * The role on THIS roster ("Team Rep", "Manager") — server data. Not displayed:
   * since Sep 24 every official line reads "Team Rep" on the sheet.
   */
  role_name: string | null
  image?: string | null
  /** False once the person no longer qualifies: the line keeps its history, takes no new money. */
  is_current: boolean
  is_registering_rep: boolean
  is_daysmart_manager: boolean
  /** The viewer is this line's payer. */
  viewer_is_payer: boolean
  /** The viewer pays this line and is not a league admin: they may raise it, never lower, cancel or remove it (D11). */
  viewer_restricted: boolean
}

/**
 * Sep 23 — a team official who may have a budget line and has none yet: shown as a
 * $0 row. Typing an amount opens their line. Keyed by `payer_user_id`.
 */
export interface BalanceOfficialCandidate {
  payer_user_id: string
  team_official_uid: string | null
  name: string
  role_name: string | null
  image?: string | null
  is_registering_rep: boolean
  is_daysmart_manager: boolean
}

/**
 * Sep 23 — what a change to an official line does to the UNINVOICED player shares.
 * Money in DOLLARS like the rest of the sheet, except the `_cents` totals.
 *
 * `none` changes nobody (the unallocated gap absorbed it); `proportional` takes the
 * extra from each uninvoiced share in proportion (Auto off); `resplit` re-splits
 * them evenly (Auto on).
 */
export interface PlayerPoolEffect {
  mode: 'none' | 'proportional' | 'resplit'
  rebalanced_cents: number
  freed_to_unallocated_cents: number
  /** `name` is null for an open slot — a share with nobody in it yet. */
  lines: Array<{
    slot_id: string
    name: string | null
    before: number
    after: number
  }>
}

/** Sep 23 — what an official write actually did (on the write's response). */
export interface BalanceRebalance {
  mode: PlayerPoolEffect['mode']
  rebalanced_cents: number
  /** What became Unallocated — the "$X is now unallocated" toast shows only when > 0. */
  freed_to_unallocated_cents: number
}

/** Sep 23 — the preview every change to an official line is confirmed with. */
export interface OfficialLinePreview {
  amount: number
  player_pool_effect: PlayerPoolEffect
  /** Submit with the change; the server 422s `preview_changed` when anything moved since. */
  fingerprint: string
}

/** Sep 23 — undoing an official's added invoice: what goes back to the player shares. */
export interface OfficialRemovalPreview {
  player_pool_effect: PlayerPoolEffect
  fingerprint: string
}

/**
 * SN-4709 follow-up (Command Center) — a roster position (PlayerType). The server
 * shapes it through PlayerTypeResource, so `name` is the short code ("FWD") and
 * `name_full` is the readable label ("Forward") — prefer name_full for display.
 */
export interface BalanceSlotPosition {
  id: string
  name: string
  name_full?: string | null
  abbreviation?: string | null
}

export interface BalanceSlotPlayer {
  id: string
  name_full: string
  image?: string | null
  /**
   * SN-4709 follow-up (Command Center) — the occupant's roster jersey number and
   * position, read from the (roster, player) pivot. An occupant not on the roster
   * yet shows what approval will roster them with (SN-8467; Inon, Oct 2 — their own
   * answer, else the hold's). Null when there is none.
   */
  player_number?: number | null
  position?: BalanceSlotPosition | null
}

export interface BalanceBillingSchedule {
  uid: string
  payment_type: string
  due_date: string
  subtotal: number
  /**
   * The tax stored on this invoice row. Tax on top: new budget invoices add it to
   * the pre-tax `subtotal`; invoices issued before that keep the tax they were
   * issued with, so never recompute it from `subtotal` and a rate.
   */
  taxtotal?: number
  total: number
  paid: boolean
  paid_at?: string | null
  paid_in_person: boolean
  refunded: boolean
  // SN-4709 Phase 7.1/8R server authority — never infer from provider / charge.refunded.
  can_pay_online?: boolean
  can_refund?: boolean
  // SN-4709 R3-F7: 'processing' while a refund operation is in flight (pending/unknown) —
  // the affordance is suspended (can_refund=false) and the UI shows a processing state.
  refund_status?: 'processing' | null
  /** What the invoice is for. SN-8310 — the rep's description on an added invoice. */
  description?: string | null
  /**
   * SN-8310 — server authority for undoing an ADDED invoice from the balance sheet:
   * true only while it is unpaid, not being charged, not split and not named by a
   * pending offline payment. Absent on every other row and outside the sheet.
   */
  can_remove?: boolean
  [key: string]: unknown
}

// SN-4709 Phase 8: one settled bill inside an offline payment batch (immutable snapshot).
export interface OfflinePaymentApplication {
  id: string
  type: 'whole' | 'split' | 'surplus'
  bill_type: string | null
  bill_description: string | null
  applied_subtotal: number
  applied_tax: number
  applied_total: number
}

// SN-4709 Phase 8: a recorded offline payment batch on a slot's current player.
export interface OfflinePaymentBatch {
  id: string
  amount: number
  currency_code: string
  source: string
  note: string | null
  /**
   * A1 — the rep's reference/ID for this payment (cheque no., terminal ref).
   * Unlike `note` it is NOT internal: it also prints on the player's receipt.
   */
  reference: string | null
  origin: string
  recorded_at: string | null
  recorded_by?: string | null
  voided_at: string | null
  // D39 verification state. Precedence voided > verified > pending, so a batch
  // that was verified and later voided reads as 'voided'.
  verification_status: 'pending' | 'verified' | 'voided'
  verification_disposition:
    | 'pre_lock_credit'
    | 'applied'
    | 'applied_with_surplus'
    | 'surplus_only'
    | 'legacy_settled'
    | null
  verified_at: string | null
  verified_by?: string | null
  // Server-authoritative STATE half only. The caller's authority is target-aware
  // and enforced at the endpoint, so a 403 there is final — never assume this
  // flag alone means the current user may verify.
  can_verify: boolean
  /**
   * Sep 23 (D8) — why a caller who could otherwise verify may not: `own_line` when the
   * cash was recorded on the caller's OWN budget line and they are not a league admin.
   */
  verify_blocked_reason?: 'own_line' | null
  /**
   * Sep 23 — the kind of line this cash was RECORDED on (its original registration's),
   * so it survives a release and holds on the detached history. Verify and void send it
   * back as `expected_kind`. Absent on an older api2, which means `player`.
   */
  line_kind?: BalanceLineKind
  // R5-#6: server-authoritative — false (with a reason code) when later dependent activity
  // makes the void deterministically rejectable; the UI never offers it.
  can_void: boolean
  void_blocked_reason?: 'later_activity' | null
  applications: OfflinePaymentApplication[]
  // SN-4709 R3-F4: who the batch was recorded FOR — present on the registration-level
  // detached history, where the player is no longer derivable from the slot occupant.
  player?: { id: string | null; name_full: string | null } | null
}

// SN-4709 Phase 8 (C15): the server-computed preview of what an amount would settle.
export interface OfflinePaymentPreview {
  amount: number
  applications: Array<{
    bill_uid: string
    bill_type: string
    action: 'settle' | 'split'
    applied_total: number
    bill_total: number
  }>
  surplus: number
  fully_applied: boolean
  /**
   * D39 — pins the exact plan shown here. Recording REQUIRES it, and the server
   * revalidates it under the record locks, so a plan that moved underneath the
   * admin is refused instead of silently settling something else.
   */
  settlement_fingerprint: string
}

export interface BalanceSlot {
  id: string
  /** Sep 23 — absent on an older api2, which means `player`. */
  kind?: BalanceLineKind
  /** Sep 23 — the official line's person; null on a player's share. */
  official?: BalanceSlotOfficial | null
  amount: number
  /**
   * SN-4709 (James, Jul 31) — the ADVISORY even share of the division fee, beside
   * what the rep actually allocated (`amount`). Server-computed with the same
   * penny-exact allocator the re-split uses, so the column sums to the fee.
   * Null on a cancelled slot (excluded from the split) and on an older api2.
   */
  even_split?: number | null
  locked_at: string | null
  status: BalanceSlotStatus
  /** Jeff, Oct 1 — see `BalanceSlotType`. */
  type?: BalanceSlotType | null
  /** Jeff, Oct 1 — set only on an unclaimed invited slot. */
  invitee?: BalanceSlotInvitee | null
  player: BalanceSlotPlayer | null
  player_registration_id: string | null
  /**
   * Jeff, Sep 24 — when the slot's CURRENT occupant joined: a player's own
   * registration, or when an official's line was opened. Not the slot's age:
   * slots are reused in place. Null on an open slot. Absent on an older api2,
   * whose joined rows then keep the server's id order. Read only through
   * `balanceRowOrder`.
   */
  joined_at?: string | null
  /**
   * A2 — the rep's INTERNAL note about the slot's current player. Stored on that
   * player's own registration (a slot is reusable), and never shown to players.
   */
  note?: string | null
  paid_online: number
  paid_in_person: number
  /**
   * Tax on top — cash INCLUDING tax, from the server's balance ledger: a locked
   * slot's live invoice totals, or what locking an open/pending slot would invoice
   * at the current rate. `amount` stays the pre-tax share. Optional so a payload
   * from an older server still renders — fall back to `amount`, and never add tax
   * on the client.
   */
  expected_total?: number
  expected_tax?: number
  /** Cash including tax (`expected_total − paid`) — negative is a credit. */
  owed: number
  billing_schedules: BalanceBillingSchedule[]
  offline_payments?: OfflinePaymentBatch[]
  /**
   * SN-4709 × SN-8081 — is this OPEN slot listed in the competition's Player
   * Finder? Jeff, Oct 1: part of the slot's TYPE (`player_finder`), never its
   * status. An older api2 sends no `type`, so `balanceRowKind.slotType` derives it
   * from this flag.
   *
   * C9: losing approval clears the flag server-side, so this is always the
   * EFFECTIVE state — it can never claim an ad that is not running.
   */
  finder_listed?: boolean
  /**
   * Listed AND default Player Finder search shows the ad — whether it actually
   * runs. False while the team's recruiting switch is off (the switch never
   * unticks slots), the competition has Player Finder turned off, or the roster
   * is full; `player_finder_hidden_reasons` on the registration says which.
   * Absent on an older server, where it is read as `finder_listed`.
   */
  finder_advertised?: boolean
  /**
   * The position the slot is recruiting for. ADVISORY (C2): it filters who sees
   * the ad and never steers who lands in the slot. Null means "any player".
   * Present only on listed OPEN slots.
   */
  finder_position?: BalanceSlotPosition | null
  /**
   * Inon, Oct 2 — whether THIS viewer may double-click the row's Position and
   * Number: someone who may edit the roster (Edit Player's check), on a player who
   * is on the roster or a hold nobody has claimed yet. Server-decided like the
   * money flags; absent on an older api2, which leaves both read-only.
   */
  can_edit_roster_details?: boolean
}

export interface BalanceKpis {
  allocated: number
  unallocated: number
  outstanding: number
  paid_online: number
  paid_online_7d: number
  paid_in_person: number
  total_paid: number
  threshold_met: boolean
  /**
   * Tax on top — what the team owes in cash INCLUDING tax: invoice totals for
   * invoiced slots plus the current-rate estimate for everything else. Progress
   * denominators use this, never `team_cost`. Optional: an older server omits it.
   */
  expected_total?: number
  expected_tax?: number
  /**
   * The qualifying minimum in cash INCLUDING tax. Null when the division has no
   * threshold; absent on an older server (fall back to `threshold`).
   */
  threshold_total?: number | null
}

/**
 * The division's payment plan for this sheet, as the server resolves it.
 *
 * SN-4709 (Jeff, Sep 1) — the distributed schedule is a PLAYER plan
 * (`player_installments` and siblings) with the legacy team columns as a
 * fallback; the server resolves the two shapes into this one before sending it,
 * so the client never sees which shape a division stored.
 *
 * `deposit` is the MINIMUM TO QUALIFY — the division total, pro-rated per slot
 * at lock time by that slot's share of the team cost. It is NOT the division's
 * `deposit` column, which belongs to the non-distribution generator.
 */
export interface BalanceInstallmentConfig {
  deposit: number
  installments: number
  installment_frequency: string | null
  installment_start_date: string | null
  /** Whole share due at lock, no schedule — the installment editor is off. */
  single_payment: boolean
  /**
   * SN-8310 — a custom plan's payment dates (Y-m-d); empty for any other plan.
   * Optional so a client briefly running against an older API still type-checks.
   */
  installment_dates?: string[]
}

/**
 * SN-4709 follow-up — the TEAM-level payment status codes.
 *
 * Deliberately NOT part of `BalanceSlotStatus`: that union tracks a SLOT's
 * lifecycle (open → pending → confirmed → paid / cancelled) and drives the grid's
 * sort ranks. This is a sibling read-model key describing the whole registration.
 *
 * Display only — "Not Qualified" is an indicator, it does not block roster,
 * scheduling or play.
 */
export type TeamPaymentStatusCode =
  | 'paid_in_full'
  | 'minimum_payment_due'
  | 'not_qualified'
  | 'qualified_balance_due'
  | 'balance_overdue'

/** Which of the competition's own dates a cutoff was derived from. */
export type TeamPaymentCutoffSource = 'registration_end' | 'competition_start'

/**
 * One competition date on the wire.
 *
 * Split out from `TeamPaymentCutoff` because not every date the header renders is
 * a DEADLINE: the collection window's opening day is measured against nothing, so
 * it has no `source`. Everything else about the two is identical, including the
 * `on`-not-`at` display rule below.
 */
export interface TeamPaymentDate {
  /** The instant, UTC. Used for ordering/debugging, NOT for display. */
  at: string
  /**
   * The calendar day (`YYYY-MM-DD`) the date falls on in the COMPETITION's own
   * timezone — what the copy must render. `at` alone cannot be formatted back to
   * this day: competition start is anchored to midnight in the account's zone, so
   * a viewer in any other zone formatting `at` lands on the wrong date. Optional
   * so a payload from an older server still renders (the badge falls back to `at`).
   */
  on?: string
}

export interface TeamPaymentCutoff extends TeamPaymentDate {
  source: TeamPaymentCutoffSource
}

export interface TeamPaymentStatus {
  /**
   * Widened past the union on purpose: the server owns the vocabulary, so a code
   * this build has never heard of must degrade to "no badge", never throw.
   */
  code: TeamPaymentStatusCode | (string & {})
  threshold_met: boolean
  /**
   * DERIVED on every read from the root registration's end and the competition's
   * start (never persisted), and returned only so the tooltip can name the date
   * actually driving the status.
   */
  cutoffs: {
    qualification: TeamPaymentCutoff
    final: TeamPaymentCutoff
  }
  /**
   * SN-4709 Season Runway — the day the collection window OPENED (the root
   * registration's `starts_at`), which the header's elapsed-time lane is drawn
   * from. Not a cutoff: nothing is measured against it, so it carries no `source`
   * and sits beside `cutoffs` rather than inside it.
   *
   * Null when the competition has no registration start, and absent entirely on a
   * payload from an older server — the runway then falls back to a plain
   * collection bar rather than inventing an opening day.
   */
  registration_opens?: TeamPaymentDate | null
  /**
   * SN-4709 Season Runway (Inon, Sep 2) — the day the COMPETITION starts, for the
   * window lane's marker.
   *
   * Its own field rather than something read off `cutoffs.final.source`: `final`
   * is max(registration end, competition start), so a competition whose
   * registration runs past its opener never names the start date in the cutoffs at
   * all. Deriving the marker from them would make it vanish on exactly the data
   * where a season opener matters most.
   *
   * Optional and nullable so a payload from an older server simply draws no
   * marker.
   */
  competition_starts?: TeamPaymentDate | null
}

export interface BalanceRegistration {
  id: string
  division: {
    id: string
    name: string
    root_name: string | null
  }
  currency: {
    code: string
    /** The currency's country (CountryResource) — its locale formats money inputs. */
    country: CountryLocaleInput | null
  }
  tax_rate: number
  team_cost: number
  threshold: number | null
  // The division's Team Payments plan (D37) — drives the Lock modal preview.
  // `deposit` is the division total; the modal pro-rates it per slot at lock time.
  installment_config: BalanceInstallmentConfig
  auto: boolean
  locked: boolean
  /**
   * SN-4709 Team Privacy. A private team is UNLISTED: it does not appear in the
   * player-facing picker, and anyone holding `share_link` can join it
   * (Inon + Pouya, Sep 17 — the link is a deep link, not a credential).
   *
   * `registration_status` is the REAL row status (RegistrationStatusType), not the
   * competition's approval CONFIGURATION — with auto-approve split per registration
   * type, a config-derived status goes stale the moment the two halves disagree.
   *
   * `share_link` comes from api2's one canonical builder, shared with the Review
   * step and the confirmation email. Never compose it client-side.
   *
   * All three are optional so an older API simply omits them.
   */
  is_private?: boolean
  registration_status?: number
  share_link?: string | null
  /**
   * SN-4709 × SN-8081 (D48) — ONE server-computed answer: the competition's
   * Player Finder is on AND this registration is approved (C8) AND no other
   * registration of the team is already recruiting here (C7).
   *
   * It gates OPT-IN only — the Add Player Slot checkbox and "List in Player
   * Finder…". NEVER gate "Remove from Player Finder" on an already-listed slot
   * with it: turning the competition's finder off does not clear flags, so doing
   * so would trap a rep with an ad they cannot withdraw.
   */
  can_list_in_player_finder?: boolean
  /**
   * Jeff, Oct 1 — whether slots can be held for invited players: the team
   * registration is approved. Cancelling an invite never depends on it. Absent on
   * an older api2.
   */
  can_invite_players?: boolean
  /**
   * The team's Player Finder MASTER switch in this competition. Off means no slot
   * is advertised, whatever it is ticked for. Listing another slot turns it back
   * on; the Team page's Player Finder settings manage it directly.
   */
  player_finder_recruiting?: boolean
  /**
   * Why this registration's listed open slots are not advertised, in the Player
   * Finder settings' own codes (`recruiting_off`, `finder_disabled`,
   * `roster_full`, …). Empty while every listed slot is advertised, and when
   * another registration of the team runs the competition's ad. Absent on an
   * older server.
   */
  player_finder_hidden_reasons?: string[]
  kpis: BalanceKpis
  /**
   * SN-4709 follow-up — one status per DIVISION registration, never aggregated
   * across divisions. Null whenever the server cannot resolve the qualifying
   * threshold, the registration end or the competition start — it never invents a
   * cutoff, and an older API simply omits the key.
   */
  payment_status?: TeamPaymentStatus | null
  slots: BalanceSlot[]
  // SN-4709 R3-F4: offline batches whose slot/player pairing no longer holds (released
  // players, reassigned/deleted slots) — never rendered under a slot's new occupant.
  offline_payment_history?: OfflinePaymentBatch[]
  /**
   * Sep 23 — the team officials who may have a budget line here and have none: shown
   * as $0 rows. Derived on every read; nothing exists until an amount is typed.
   */
  official_candidates?: BalanceOfficialCandidate[]
}

export interface TeamBalancePayload {
  team_registrations: BalanceRegistration[]
  /** Sep 23 — what an official write did to the player shares (write responses only). */
  rebalance?: BalanceRebalance
}

const unwrap = <T = TeamBalancePayload>(response: { data: T }): T =>
  response.data

/**
 * SN-4709 D40 — one roster owns one balance sheet, so every sheet call is
 * roster-scoped. A roster that does not exist or belongs to another team is 404;
 * an own-team roster with nothing visible is 200 + an empty list (never a 403),
 * so callers must distinguish "no such roster" from "nothing here for you".
 */
const balanceBase = (teamId: string, rosterId: string) =>
  `/teams/${teamId}/rosters/${rosterId}/balance`

/**
 * Sep 23 (R2-4) — the line kind a money write names. The page sends the kind of
 * the row it DISPLAYED, and the server 409s `refresh_required` when it is missing
 * or wrong for an official line — so a page that renders an official line as a
 * player's share (one built before official lines) can never move money on it.
 * Player lines accept it or its absence.
 */
const kindField = (expectedKind?: BalanceLineKind) =>
  expectedKind ? { expected_kind: expectedKind } : {}

/** D39 — an admin's re-review of a pending offline payment before verifying it. */
export interface VerifyPreview {
  amount: number
  /**
   * A1 — what the rep wrote down when they took the money. This is the surface
   * where an admin decides whether it was really received, so both are shown
   * here; the internal note still never reaches the player's receipt.
   */
  reference: string | null
  note: string | null
  applications: Array<{
    bill_uid: string
    bill_type: string
    action: string
    applied_total: number
    bill_total: number
  }>
  surplus: number
  fully_applied: boolean
  settlement_fingerprint: string
  recorded_fingerprint: string | null
  drifted: boolean
  /**
   * `settlement_plan_changed` is confirmable; `settlement_target_changed` is NOT —
   * the UI must not offer a confirm for it, because no confirmation can authorise
   * paying a different person or currency. Recovery is void + re-record.
   */
  drift_class: 'settlement_plan_changed' | 'settlement_target_changed' | null
  unavailable_bill_uids: string[]
}

export const previewVerifyOfflinePayment = async (
  teamId: string,
  rosterId: string,
  batchId: string,
  /** The batch's own `line_kind` (Sep 23) — never inferred from the row it sits under. */
  expectedKind?: BalanceLineKind
): Promise<VerifyPreview> => {
  return unwrap<VerifyPreview>(
    await req(
      `${balanceBase(
        teamId,
        rosterId
      )}/offline-payments/${batchId}/verify-preview`,
      { query: { expected_kind: expectedKind } }
    )
  )
}

/**
 * Verify a pending offline payment. `settlementFingerprint` confirms a plan the
 * admin has just re-reviewed; omit it to verify against the plan as recorded.
 * Single transport attempt — verification settles money.
 */
export const verifyOfflinePayment = async (
  teamId: string,
  rosterId: string,
  batchId: string,
  settlementFingerprint?: string,
  /** The batch's own `line_kind` (Sep 23). */
  expectedKind?: BalanceLineKind
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(
      `${balanceBase(teamId, rosterId)}/offline-payments/${batchId}/verify`,
      {
        method: 'POST',
        body: JSON.stringify({
          ...(settlementFingerprint
            ? { settlement_fingerprint: settlementFingerprint }
            : {}),
          ...kindField(expectedKind),
        }),
        // ONE attempt, never re-sent: verification settles money, so a retry could
        // race a slow first response. NOTE 1, not 0 — request() loops
        // `attempt < retry`, so 0 executes the body zero times, returns undefined,
        // and the caller then blows up reading `.status`.
        transportRetries: 1,
      }
    )
  )
}

/** Minimal roster metadata for the balance roster switcher (D40 / A-3). */
export interface BalanceRoster {
  id: string
  /** The ROOT SCHEDULE's name — what the Roster tab shows for the same roster. */
  name: string | null
  schedule_name: string | null
  /**
   * SN-8467 — the root schedule's uid, which a `?sn_schedule=` link carries (the rep's
   * "Go To My Team"). Absent from an api2 that predates it.
   */
  schedule_uid?: string | null
  starts_at: string | null
  ends_at: string | null
  registration_count: number
}

export interface BalanceRostersPayload {
  rosters: BalanceRoster[]
}

/**
 * Which rosters does the caller actually have balance on? Team-level and
 * manageBalance-gated on the server (deliberately NOT the roster index, which is
 * only `view`-gated and lists every roster of the team). Drives the switcher and
 * the default roster on a direct reload.
 */
export const getBalanceRosters = async (
  teamId: string
): Promise<BalanceRostersPayload> => {
  return unwrap<BalanceRostersPayload>(
    await req(`/teams/${teamId}/balance/rosters`)
  )
}

export const getTeamBalance = async (
  teamId: string,
  rosterId: string
): Promise<TeamBalancePayload> => {
  return unwrap(await req(`${balanceBase(teamId, rosterId)}`))
}

export const createBalanceSlots = async (
  teamId: string,
  rosterId: string,
  body: {
    team_registration_id: string
    count: number
    /**
     * SN-4709 × SN-8081 (D46) — the Add Player Slot modal's opt-in. Never
     * inferred: listing a team in the Player Finder is a public act, so the
     * checkbox defaults OFF and only an explicit tick sends true.
     *
     * Deploy api2 BEFORE the client — an older validator silently discards these
     * two fields and creates an unlisted slot.
     */
    finder_listed?: boolean
    finder_player_type_id?: string | null
  }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

/**
 * What a listed-slot save MEANS (review round 1, TF-02). Both send
 * `finder_listed: true`, so the slot's state alone cannot tell them apart:
 *
 * - `list` — "List in Player Finder…": consent to advertise. Listing an unlisted
 *   slot turns the team's recruiting back on.
 * - `edit_position` — "Edit Player Finder Position": only the position. Never
 *   consent; if the slot was unlisted meanwhile the server answers 409 rather
 *   than listing it again.
 */
export type SlotFinderAction = 'list' | 'edit_position'

export type SlotFinderListingBody =
  | {
      finder_listed: true
      finder_action: SlotFinderAction
      finder_player_type_id?: string | null
    }
  | { finder_listed: false; finder_player_type_id?: null }

/**
 * SN-4709 × SN-8081 (D46) — tick / untick ONE open slot's Player Finder listing.
 *
 * Opt-IN is server-gated (approved registration, competition finder on, no other
 * recruiting registration) and 422s with a field-keyed message when refused.
 * Untick is ALWAYS permitted, so a rep can withdraw an ad even after the
 * competition disables its finder.
 */
export const updateSlotFinderListing = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: SlotFinderListingBody
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/finder`, {
      method: 'PUT',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

export const updateBalanceSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: {
    amount: number
    /**
     * Sep 23 — an official line's amount is PREVIEWED first
     * (`previewOfficialAmount`); the change carries that preview's fingerprint.
     */
    preview_fingerprint?: string
    expected_kind?: BalanceLineKind
  }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

/**
 * SN-4709 A2 — the rep's internal note about the slot's CURRENT player.
 *
 * Separate from updateBalanceSlot on purpose: a note is not money, so the server
 * accepts it on a locked slot, a locked sheet and an Auto-on sheet — all three of
 * which the amount endpoint rejects.
 *
 * `expectedPlayerRegistrationId` is the occupant the editor was bound to when it
 * mounted, NEVER re-derived at save time. If the slot was reassigned in the
 * meantime the server 409s rather than filing one player's note against another.
 */
export const updateSlotNote = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  note: string | null,
  expectedPlayerRegistrationId: string
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/note`, {
      method: 'PUT',
      body: JSON.stringify({
        note,
        expected_player_registration_id: expectedPlayerRegistrationId,
      }),
      transportRetries: 1,
    })
  )
}

/**
 * Inon, Oct 2 — what a double-clicked Position or Number sends. Only the field that
 * changed travels; `player_number: null` clears the number. Position can only be
 * changed, never cleared (the roster row requires one).
 *
 * The expectations are the row as the rep saw it, never re-read at save time: the
 * occupant's registration, or — on a hold nobody has claimed (null) — who it was
 * held for. The server 409s when either changed meanwhile, rather than writing a
 * number meant for one person against another.
 */
export interface SlotRosterDetailsInput {
  player_type_id?: number
  player_number?: number | null
  expected_player_registration_id: string | null
  /** Sent only with `expected_player_registration_id: null`. */
  expected_invitee?: { email: string; name_first: string; name_last: string }
}

/**
 * Inon, Oct 2 — a slot's Position and Number, edited on the sheet. Not money: the
 * server accepts it on an invoiced slot, a locked sheet and an Auto sheet, and
 * decides under its own locks where it lands — the player's roster row, or the hold.
 */
export const updateSlotRosterDetails = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: SlotRosterDetailsInput
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(
      `${balanceBase(teamId, rosterId)}/slots/${slotId}/roster-details`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        transportRetries: 1,
      }
    )
  )
}

export const deleteBalanceSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}`, {
      method: 'DELETE',
      transportRetries: 1,
    })
  )
}

/**
 * Jeff, Oct 1 — Add → Invited Player Slot: one new slot per invitee (at most 20),
 * each held for them; re-splits when Auto is on. Field errors come back keyed
 * `invitees.N.field`.
 */
export const createInvitedSlots = async (
  teamId: string,
  rosterId: string,
  body: { team_registration_id: string; invitees: InviteeInput[] }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/invites`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

/** ⋮ Invite Player — hold this open slot for someone. The slot keeps its share. */
export const inviteIntoSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  invitee: InviteeInput
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/invite`, {
      method: 'POST',
      body: JSON.stringify(invitee),
      transportRetries: 1,
    })
  )
}

/**
 * Change Invitee — a new address gets a new invite and the old link stops working;
 * the same address only corrects the name.
 */
export const changeSlotInvitee = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  invitee: InviteeInput
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/invite`, {
      method: 'PUT',
      body: JSON.stringify(invitee),
      transportRetries: 1,
    })
  )
}

/** Cancel Invite — the slot opens back up for anyone and keeps its share. */
export const cancelSlotInvite = async (
  teamId: string,
  rosterId: string,
  slotId: string
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/invite`, {
      method: 'DELETE',
      transportRetries: 1,
    })
  )
}

/** Resend Invite — refused (422) within 10 minutes of the last send. */
export const resendSlotInvite = async (
  teamId: string,
  rosterId: string,
  slotId: string
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(
      `${balanceBase(teamId, rosterId)}/slots/${slotId}/invite/resend`,
      {
        method: 'POST',
        transportRetries: 1,
      }
    )
  )
}

/**
 * Jeff, Oct 1 — a previous season a rep may import players from: one team's roster
 * in one competition, named by the roster's uid (the client's season convention,
 * see `registrationSeasons`). The rep's other teams count too, so the team rides
 * along.
 */
export interface ImportSeason {
  id: string
  schedule_name: string | null
  schedule_uid: string | null
  starts_at: string | null
  ends_at: string | null
  team: { id: string; name: string; is_this_team: boolean }
  /** Its regulars: spares are left out. */
  player_count: number
}

export interface ImportSeasonsPayload {
  seasons: ImportSeason[]
}

/**
 * Where a previous season's player stands on this season's sheet, as the server
 * decides it: the rep, already registered (pending included), on the roster,
 * already invited, without any email on file, or free to invite.
 */
export type ImportPlayerStatus =
  | 'team_rep'
  | 'registered'
  | 'on_roster'
  | 'invited'
  | 'no_email'
  | 'available'

/** Which record the invite address came from, first valid one wins. */
export type ImportEmailSource =
  | 'account'
  | 'guardian'
  | 'roster_invite'
  | 'player'
  | 'guardian_contact'

export interface ImportSeasonPlayer {
  /** The player's uid. */
  id: string
  name_first: string | null
  name_last: string | null
  image: string | null
  /** The position they played, or the sport's default when this sport lacks it. */
  position: BalanceSlotPosition | null
  is_spare: boolean
  email: string | null
  email_source: ImportEmailSource | null
  status: ImportPlayerStatus
}

/** The division's player cap and what is left of it; null when it caps nobody. */
export interface ImportRoom {
  max_players: number
  remaining: number
}

export interface ImportSeasonPlayersPayload {
  room: ImportRoom | null
  players: ImportSeasonPlayer[]
}

/** One player to invite: their uid, and the email typed for one with none on file. */
export interface ImportPlayerInput {
  id: string
  email?: string
}

/** The previous seasons, newest first, at most five. */
export const getImportSeasons = async (
  teamId: string,
  rosterId: string,
  teamRegistrationId: string
): Promise<ImportSeasonsPayload> => {
  return unwrap<ImportSeasonsPayload>(
    await req(`${balanceBase(teamId, rosterId)}/import/seasons`, {
      query: { team_registration_id: teamRegistrationId },
    })
  )
}

/** One season's players, and the room the division leaves the team. */
export const getImportSeasonPlayers = async (
  teamId: string,
  rosterId: string,
  teamRegistrationId: string,
  seasonId: string
): Promise<ImportSeasonPlayersPayload> => {
  return unwrap<ImportSeasonPlayersPayload>(
    await req(
      `${balanceBase(teamId, rosterId)}/import/seasons/${seasonId}/players`,
      { query: { team_registration_id: teamRegistrationId } }
    )
  )
}

/**
 * Send N Invites — one held slot per player, each emailed; re-splits when Auto is
 * on. Field errors come back keyed `players.N.field` (`players.N` for a player who
 * can no longer be invited).
 */
export const importSlots = async (
  teamId: string,
  rosterId: string,
  body: {
    team_registration_id: string
    season_id: string
    players: ImportPlayerInput[]
  }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/import`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

export const autoDistribute = async (
  teamId: string,
  rosterId: string,
  body: { team_registration_id: string }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/auto-distribute`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

export const updateBalanceSettings = async (
  teamId: string,
  rosterId: string,
  body: {
    team_registration_id: string
    auto?: boolean
    locked?: boolean
    /**
     * SN-4709 Team Privacy — carried on the EXISTING settings contract rather than a
     * new endpoint: its roster- and registration-scoped authorization is already
     * exactly what this screen needs. Omitting the key leaves privacy untouched.
     */
    is_private?: boolean
  }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/settings`, {
      method: 'PUT',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
}

/**
 * Phase 6 slot lifecycle: lock ⇒ generates the player's billing rows; unlock
 * (D21) soft-deletes unpaid generated rows; release reopens the slot. The {slot}
 * route param carries the target; lock additionally sends the REQUIRED
 * preview_fingerprint body (F4 — the server rejects a lock without it), while
 * unlock/release have no body. Single transport attempt: locking inserts rows, so
 * a dropped-but-committed response must never be re-sent.
 */
export const lockBalanceSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  previewFingerprint: string,
  expectedKind?: BalanceLineKind
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/lock`, {
      method: 'POST',
      // R5-#2 / F4: the fingerprint is REQUIRED and always sent — the server revalidates the
      // previewed schedule under its locks, so a stale confirm (amount/occupant/config/credit
      // changed) 422s instead of locking blind. The API rejects a request without it.
      body: JSON.stringify({
        preview_fingerprint: previewFingerprint,
        ...kindField(expectedKind),
      }),
      transportRetries: 1,
    })
  )
}

// R4-#4: one row the real lock will generate — server-exact money + due date, with the
// pre-lock-credit consumption the lock will perform (settled/partially-covered rows).
export interface LockPreviewRow {
  type: string
  installment_number: number
  subtotal: number
  tax: number
  total: number
  due_date: string
  credit_applied: number
  settled_by_credit: boolean
  remaining_total: number
}

/** Tax on top — the whole schedule: pre-tax share, the tax added, and the total. */
export interface LockPreviewTotals {
  subtotal: number
  tax: number
  total: number
}

export interface LockPreview {
  rows: LockPreviewRow[]
  /**
   * Sep 23 — `one_time` for a team official's line: ONE charge, tax on top, due the
   * day it is sent. Absent (an older api2) or `division` for a player's share.
   */
  schedule_shape?: 'division' | 'one_time'
  /** Absent on an older server — the modal then sums nothing and shows rows only. */
  totals?: LockPreviewTotals
  credit_total_cents: number
  credit_surplus_cents: number
  // D37: the resolved Team Payments frequency the freshly-computed rows used — the modal
  // renders THIS, not the (possibly stale) React-Query-cached balance config.
  installment_frequency: string | null
  // R5-#2: submit with the lock; the server 422s when the schedule changed since preview.
  fingerprint: string
}

/**
 * R4-#4: the EXACT, credit-aware server preview of what locking a slot will generate — the
 * same schedule calculator + credit allocator as the lock itself. A slot that cannot lock returns the same
 * field-keyed 422 the lock would.
 */
export const previewLockSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  expectedKind?: BalanceLineKind
): Promise<LockPreview> => {
  return unwrap<LockPreview>(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/lock-preview`, {
      query: { expected_kind: expectedKind },
    })
  )
}

/**
 * SN-4709 (James, Jul 31) — one preview row per SELECTED slot, so the rep sees
 * exactly who is about to be invoiced (and who will be skipped) before
 * confirming. `error` non-null ⇒ this slot cannot be invoiced right now and
 * carries no fingerprint.
 */
export interface BatchLockPreviewRow {
  slot_id: string
  player_name: string | null
  amount: number | null
  payment_count: number
  first_due_date: string | null
  /** Tax on top — the pre-tax share and the tax added to it. Absent on an older server. */
  subtotal?: number
  tax?: number
  /** What the invoices charge, tax included. */
  total: number
  fingerprint: string | null
  error: string | null
  /**
   * Sep 23 — why `error` is set, when the server names it: `refresh_required` (the
   * row's kind changed since this page loaded) or `official_not_invoiceable` (a
   * former official, or a line kept at $0 for its history).
   */
  error_reason?: string | null
  /** Sep 23 — `one_time` for a team official's line. */
  schedule_shape?: 'division' | 'one_time'
}

export interface BatchLockPreview {
  slots: BatchLockPreviewRow[]
  summary: {
    invoiceable: number
    blocked: number
    subtotal?: number
    tax?: number
    total: number
  }
}

export const previewLockBatch = async (
  teamId: string,
  rosterId: string,
  slotIds: string[],
  /** Sep 23 — slot id → the kind of the row the page displayed (R2-4). */
  expectedKinds?: Record<string, BalanceLineKind>
): Promise<BatchLockPreview> => {
  return unwrap<BatchLockPreview>(
    await req(`${balanceBase(teamId, rosterId)}/lock-preview-batch`, {
      method: 'POST',
      body: JSON.stringify({
        slot_ids: slotIds,
        ...(expectedKinds ? { expected_kinds: expectedKinds } : {}),
      }),
    })
  )
}

/**
 * The refreshed sheet PLUS a per-slot report. Best-effort by design: one slot
 * whose schedule drifted since the preview must not cost the others their
 * invoices, so `failed` is a normal 200 outcome rather than an error.
 */
export interface BatchLockResult extends TeamBalancePayload {
  locked: string[]
  /** `reason` (Sep 23): `refresh_required`, `official_not_invoiceable`, … when named. */
  failed: { slot_id: string; message: string; reason?: string | null }[]
}

/**
 * Each entry carries its OWN preview fingerprint — the same anti-drift guarantee
 * the single-slot lock has, per slot. Single transport attempt for the same
 * reason as `lockBalanceSlot`: this inserts billing rows and sends emails, so a
 * committed-but-dropped response must never be re-sent.
 */
export const lockBatch = async (
  teamId: string,
  rosterId: string,
  locks: {
    slot_id: string
    preview_fingerprint: string
    expected_kind?: BalanceLineKind
  }[]
): Promise<BatchLockResult> => {
  return unwrap<BatchLockResult>(
    await req(`${balanceBase(teamId, rosterId)}/lock-batch`, {
      method: 'POST',
      body: JSON.stringify({ locks }),
      transportRetries: 1,
    })
  )
}

export const unlockBalanceSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  expectedKind?: BalanceLineKind
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/unlock`, {
      method: 'POST',
      ...(expectedKind
        ? { body: JSON.stringify(kindField(expectedKind)) }
        : {}),
      transportRetries: 1,
    })
  )
}

export const releaseBalanceSlot = async (
  teamId: string,
  rosterId: string,
  slotId: string
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/release`, {
      method: 'POST',
      transportRetries: 1,
    })
  )
}

/**
 * SN-8310 Add Invoice — the exact charge adding `amount_cents` to an invoiced
 * player's share would create: one row, tax on top, due today in the competition's
 * timezone. Money in dollars like the rest of the sheet; `due_date` is the day at
 * midnight UTC, read in UTC.
 */
export interface AdditionalInvoicePreview {
  subtotal: number
  tax: number
  total: number
  /** Percent, e.g. 13 for 13%. */
  tax_rate: number
  due_date: string
  current_share: number
  new_share: number
  /** False when the division has no payment provider: the player pays offline. */
  can_pay_online: boolean
  /** Submit with the add; the server 422s when the charge changed since this preview. */
  fingerprint: string
  /**
   * Sep 23 (T6) — on a team official's line only: the extra is fee money, so it comes
   * out of the uninvoiced player shares, and this is what it does to them.
   */
  player_pool_effect?: PlayerPoolEffect
}

export interface AdditionalInvoiceBody {
  amount_cents: number
  description?: string | null
  preview_fingerprint: string
  expected_kind?: BalanceLineKind
}

/**
 * Read-only, so the default transport retries apply. The same guards as the add:
 * a slot that cannot take an invoice returns the add's own field-keyed 422.
 */
export const previewAdditionalInvoice = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  amountCents: number,
  expectedKind?: BalanceLineKind
): Promise<AdditionalInvoicePreview> => {
  return unwrap<AdditionalInvoicePreview>(
    await req(
      `${balanceBase(
        teamId,
        rosterId
      )}/slots/${slotId}/additional-invoice-preview`,
      { query: { amount_cents: amountCents, expected_kind: expectedKind } }
    )
  )
}

/**
 * Adds the previewed invoice and notifies the player. Single transport attempt:
 * it inserts a billing row and queues a notice, so a committed-but-dropped
 * response must never be re-sent (the fingerprint also refuses a replay).
 */
export const addAdditionalInvoice = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: AdditionalInvoiceBody
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(
      `${balanceBase(teamId, rosterId)}/slots/${slotId}/additional-invoices`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        transportRetries: 1,
      }
    )
  )
}

/**
 * Undoes an unpaid added invoice: the row goes and the share drops by its amount.
 * The player is not notified, like Cancel Invoice.
 *
 * Sep 23 — on a team official's line the undo is PREVIEWED first
 * (`previewAdditionalInvoiceRemoval`) and carries that preview's fingerprint.
 */
export const removeAdditionalInvoice = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  billUid: string,
  options: {
    expectedKind?: BalanceLineKind
    previewFingerprint?: string
  } = {}
): Promise<TeamBalancePayload> => {
  const body = {
    ...kindField(options.expectedKind),
    ...(options.previewFingerprint
      ? { preview_fingerprint: options.previewFingerprint }
      : {}),
  }

  return unwrap(
    await req(
      `${balanceBase(
        teamId,
        rosterId
      )}/slots/${slotId}/additional-invoices/${billUid}`,
      {
        method: 'DELETE',
        ...(Object.keys(body).length > 0 ? { body: JSON.stringify(body) } : {}),
        transportRetries: 1,
      }
    )
  )
}

/**
 * Sep 23 — what undoing an official's added invoice gives back to the player shares
 * (delta-based: only shares that are still the same uninvoiced player). Read-only.
 */
export const previewAdditionalInvoiceRemoval = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  billUid: string,
  expectedKind: BalanceLineKind
): Promise<OfficialRemovalPreview> => {
  return unwrap<OfficialRemovalPreview>(
    await req(
      `${balanceBase(
        teamId,
        rosterId
      )}/slots/${slotId}/additional-invoices/${billUid}/removal-preview`,
      { query: { expected_kind: expectedKind } }
    )
  )
}

/**
 * SN-4709 Phase 8 offline payments. The amount is wired in exact CENTS (C8) with a
 * client-generated idempotency_key so a dropped-but-committed response is never double-applied
 * (retry: 0 + transportRetries: 1). Record/void return the refreshed balance; preview (C15) is
 * read-only and returns only what the amount would settle — the client never computes split math.
 */
export interface OfflinePaymentBody {
  amount_cents: number
  source: string
  note?: string | null
  /** A1 — joins the request fingerprint: a same-key replay that changes it 409s. */
  reference?: string | null
  idempotency_key: string
  bill_uids?: string[]
  /** D39 — taken from the preview the admin is looking at; required to record. */
  settlement_fingerprint?: string
  /** Sep 23 — the kind of the row the cash is recorded on. */
  expected_kind?: BalanceLineKind
}

export const recordOfflinePayment = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: OfflinePaymentBody
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(
      `${balanceBase(teamId, rosterId)}/slots/${slotId}/offline-payments`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        transportRetries: 1,
      }
    )
  )
}

export const previewOfflinePayment = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  body: OfflinePaymentBody
): Promise<OfflinePaymentPreview> => {
  const response = await req(
    `${balanceBase(teamId, rosterId)}/slots/${slotId}/offline-payments/preview`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    }
  )
  return (response as { data: OfflinePaymentPreview }).data
}

export const voidOfflinePayment = async (
  teamId: string,
  rosterId: string,
  batchId: string,
  /** The batch's own `line_kind` (Sep 23). */
  expectedKind?: BalanceLineKind
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/offline-payments/${batchId}`, {
      method: 'DELETE',
      ...(expectedKind
        ? { body: JSON.stringify(kindField(expectedKind)) }
        : {}),
      transportRetries: 1,
    })
  )
}

/**
 * Sep 23 — the person a new official line is for, and what the rep typed. `payer_user_id`
 * is the candidate's; `team_official_id` is the record the row displayed, if any.
 */
export interface OfficialLineBody {
  team_registration_id: string
  payer_user_id: string
  team_official_id?: string | null
  /** Dollars, like every other amount on the sheet. */
  amount: number
}

/**
 * Sep 23 — preview opening a team official's line (or setting the one they already
 * have). Read-only; a person who may not have a line is a uniform 404.
 */
export const previewOfficialLine = async (
  teamId: string,
  rosterId: string,
  body: OfficialLineBody
): Promise<OfficialLinePreview> => {
  return unwrap<OfficialLinePreview>(
    await req(`${balanceBase(teamId, rosterId)}/official-lines/preview`, {
      method: 'POST',
      body: JSON.stringify({ ...body, expected_kind: 'official' }),
    })
  )
}

/**
 * Sep 23 — open the line the preview showed. Single transport attempt: it writes a
 * line and moves money, so a committed-but-dropped response must never be re-sent.
 * The response is the refreshed sheet plus the `rebalance` it made.
 */
export const createBalanceOfficialLine = async (
  teamId: string,
  rosterId: string,
  body: OfficialLineBody & { preview_fingerprint: string }
): Promise<TeamBalancePayload> => {
  return unwrap(
    await req(`${balanceBase(teamId, rosterId)}/official-lines`, {
      method: 'POST',
      body: JSON.stringify({ ...body, expected_kind: 'official' }),
      transportRetries: 1,
    })
  )
}

/**
 * Sep 23 — preview changing an official line's amount; the change itself is
 * `updateBalanceSlot` with this preview's fingerprint. Read-only.
 */
export const previewOfficialAmount = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  amount: number
): Promise<OfficialLinePreview> => {
  return unwrap<OfficialLinePreview>(
    await req(
      `${balanceBase(
        teamId,
        rosterId
      )}/slots/${slotId}/official-amount-preview`,
      { query: { amount, expected_kind: 'official' } }
    )
  )
}

/**
 * SN-4709 follow-up — balance COMMUNICATIONS (reminders / email / feed post).
 *
 * These deliberately differ from the sheet mutations above: they do NOT return the
 * balance payload and NEVER change balances, so callers must NOT invalidate the
 * balance cache (no new query keys either). Each is non-idempotent (a queued send
 * must not be re-sent), so it runs with a single transport attempt.
 *
 * Counts are three DISTINCT things: `target_count` (who matched), `queued_count`
 * (who had >=1 permitted channel — audience minus opt-outs / the non-prod gate) and
 * `unreachable_count` (matched but with no contact on file). The client reports what
 * the SERVER returns, never its own estimate.
 */
export interface ReminderResult {
  target_count: number
  queued_count: number
  unreachable_count: number
}

export interface BroadcastEmailResult {
  target_count: number
  queued_count: number
  unreachable_count: number
}

export interface FeedMessageResult {
  posted: boolean
  /** The committed post's uid — always returned once the post exists. */
  post_uid: string
  target_count: number
  queued_count: number
  unreachable_count: number
  /** True when the post committed but a fan-out chunk failed to enqueue. */
  enqueue_failed: boolean
}

export interface BalanceEmailBody {
  subject: string
  message: string
  include_summary: boolean
}

export interface BalanceFeedMessageBody {
  subject: string
  message: string
  /** Scopes ONLY the notify audience; the feed post itself is team-wide. */
  roster_uid: string
}

/**
 * Bulk reminder over the whole roster — server-authoritative: it reminds every
 * remindable slot the caller can see, IGNORING the client's grid filters. An
 * optional short message rides along.
 */
export const sendPaymentReminders = async (
  teamId: string,
  rosterId: string,
  message?: string | null
): Promise<ReminderResult> =>
  unwrap<ReminderResult>(
    await req(`${balanceBase(teamId, rosterId)}/reminders`, {
      method: 'POST',
      body: JSON.stringify(message ? { message } : {}),
      transportRetries: 1,
    })
  )

/** Single-slot reminder; 422 if the slot has no outstanding balance to chase. */
export const sendSlotReminder = async (
  teamId: string,
  rosterId: string,
  slotId: string,
  message?: string | null
): Promise<ReminderResult> =>
  unwrap<ReminderResult>(
    await req(`${balanceBase(teamId, rosterId)}/slots/${slotId}/remind`, {
      method: 'POST',
      body: JSON.stringify(message ? { message } : {}),
      transportRetries: 1,
    })
  )

/** "Email Team Members" — email + in-app, per-role balance privacy, no feed post. */
export const broadcastBalanceEmail = async (
  teamId: string,
  rosterId: string,
  body: BalanceEmailBody
): Promise<BroadcastEmailResult> =>
  unwrap<BroadcastEmailResult>(
    await req(`${balanceBase(teamId, rosterId)}/broadcast/email`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )

/**
 * "Post & Notify Team" — team-scoped (roster in the body). Authorized by the feed
 * `post` ability on the server, NOT manageBalance; the client hides the action when
 * the caller cannot post, but the server stays authoritative.
 */
export const postAndNotifyTeam = async (
  teamId: string,
  body: BalanceFeedMessageBody
): Promise<FeedMessageResult> =>
  unwrap<FeedMessageResult>(
    await req(`/teams/${teamId}/balance/broadcast/message`, {
      method: 'POST',
      body: JSON.stringify(body),
      transportRetries: 1,
    })
  )
